import './settings/settings-leaf.css';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiKeysApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, Checkbox, Select, TextInput } from './ui.tsx';
import {
  API_KEY_EXPIRY_DAY_CHOICES,
  API_KEY_NAME_MAX,
  presetScopes,
  type ApiScope,
} from '@shared/constants/api-keys.ts';
import { PLANS, minimumPlanFor, planHasFeature } from '@shared/constants/index.ts';
import { formatNumericInstant } from '../i18n/datetime.ts';

/**
 * API keys and connected apps, for Settings (owners only).
 *
 * A key is shown exactly once, at creation, because only its hash is stored —
 * the same rule calendar feeds and quote share links follow. The list
 * afterwards carries what is needed to decide whether a key can go: its name,
 * what it may do, when it was last used, and when it stops working.
 *
 * Access is chosen as a preset or, behind "Custom", as scopes. The scope list
 * itself comes from the API rather than from anything written here, so this
 * screen cannot offer a scope the Worker does not enforce — and the endpoint
 * list at the bottom is rendered as method and path only. That is deliberate:
 * the summaries the API serves are developer-facing English prose, and
 * printing them here would put untranslated sentences in the middle of a
 * Spanish page. A URL and a scope name read the same in both languages.
 */
export default function ApiKeysSection() {
  const { t } = useTranslation();
  const { locations, company } = useSession();
  const locationNameById = new Map(locations.map((location) => [location.id, location.name]));

  // API keys, OAuth apps, Zapier and Make are a Pro-tier capability, so below
  // that tier the management screen gives way to an upsell — the Worker would
  // refuse the credential regardless of what was minted here.
  const planGated = company !== null && !planHasFeature(company.effectivePlan, 'external_api');

  const [name, setName] = useState('');
  const [preset, setPreset] = useState<'read' | 'read_write' | 'custom'>('read');
  const [custom, setCustom] = useState<ApiScope[]>([]);
  const [expiresInDays, setExpiresInDays] = useState('');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showSurface, setShowSurface] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const keys = useQuery({ queryKey: ['api-keys'], queryFn: () => apiKeysApi.list() });
  const apps = useQuery({ queryKey: ['connected-apps'], queryFn: () => apiKeysApi.connectedApps() });

  const create = useMutation({
    mutationFn: () =>
      apiKeysApi.create({
        name,
        ...(preset === 'custom' ? { scopes: custom } : { preset }),
        expiresInDays: expiresInDays ? Number.parseInt(expiresInDays, 10) : null,
      }),
    onSuccess: (result) => {
      setNewToken(result.token);
      setName('');
      setError(null);
      void keys.refetch();
    },
    onError: (caught) => setError(caught instanceof ApiRequestError ? caught.message : t('apiKeys.createError')),
  });

  const revoke = useMutation({
    mutationFn: (keyId: string) => apiKeysApi.revoke(keyId),
    onSuccess: () => {
      setError(null);
      void keys.refetch();
    },
    onError: (caught) => setError(caught instanceof ApiRequestError ? caught.message : t('apiKeys.revokeError')),
  });

  const disconnect = useMutation({
    mutationFn: (appId: string) => apiKeysApi.disconnectApp(appId),
    onSuccess: () => {
      setError(null);
      void apps.refetch();
    },
    onError: (caught) => setError(caught instanceof ApiRequestError ? caught.message : t('apiKeys.disconnectError')),
  });

  async function copyToken() {
    if (!newToken) return;
    try {
      await navigator.clipboard.writeText(newToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('apiKeys.copyError'));
    }
  }

  /** Live, expired or revoked — what the badge beside a key's name says. */
  function statusOf(key: { revokedAt: string | null; expiresAt: string | null }): 'revoked' | 'expired' | 'active' {
    if (key.revokedAt) return 'revoked';
    if (key.expiresAt && Date.parse(key.expiresAt) <= Date.now()) return 'expired';
    return 'active';
  }

  /** "Read only", "Read and write", or "6 scopes" for anything custom. */
  function accessLabel(scopes: ApiScope[], keyPreset: string): string {
    if (keyPreset === 'read' || keyPreset === 'read_write') return t(`apiKeys.preset.${keyPreset}`);
    return t('apiKeys.scopeCount', { count: scopes.length });
  }

  const surface = keys.data?.surface ?? [];
  const scopeCatalog = keys.data?.scopes ?? [];
  const chosenScopes = preset === 'custom' ? custom : [...presetScopes(preset)];

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="api-keys-heading">
      <div className="work-section-heading">
        <h2 id="api-keys-heading" className="mb-3 text-lg font-semibold">
          {t('apiKeys.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('apiKeys.description')}</p>

      {/* Minting is Pro-tier — the Worker would refuse the create request
          regardless of what was filled in below — but revoking and
          disconnecting stay reachable on every plan, so a key or connection an
          owner no longer wants is never locked behind an upgrade. */}
      {planGated ? (
        <div className="mb-4">
          <Alert tone="info" title={t('apiKeys.upgradeTitle')}>
            <p>{t('apiKeys.upgradeNote', { plan: PLANS[minimumPlanFor('external_api')].name })}</p>
            <a href="#settings-billing" className="mt-2 inline-block font-semibold hover:underline">
              {t('apiKeys.upgradeCta')}
            </a>
          </Alert>
        </div>
      ) : null}

      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {newToken ? (
        <div className="mb-4">
          <Alert tone="success" title={t('apiKeys.createdTitle')}>
            <p className="mb-2">{t('apiKeys.createdMessage')}</p>
            <p className="mb-2 font-mono text-xs break-all">{newToken}</p>
            <div className="flex gap-2">
              <Button onClick={() => void copyToken()}>{copied ? t('common.copied') : t('apiKeys.copyKey')}</Button>
              <Button variant="ghost" onClick={() => setNewToken(null)}>
                {t('apiKeys.done')}
              </Button>
            </div>
          </Alert>
        </div>
      ) : null}

      {keys.data && keys.data.keys.length > 0 ? (
        <ul className="mb-4 divide-y divide-[color:var(--color-line)]">
          {keys.data.keys.map((key) => {
            const status = statusOf(key);
            return (
              <li key={key.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                <div>
                  <p className="font-medium">
                    {key.name}
                    <span className="ml-2 text-sm font-normal text-[color:var(--color-muted)]">
                      {accessLabel(key.scopes, key.preset)}
                    </span>
                    {status !== 'active' ? (
                      <span className="ml-2 text-sm font-normal text-[color:var(--color-danger)]">
                        {t(`apiKeys.status.${status}`)}
                      </span>
                    ) : null}
                  </p>
                  <p className="font-mono text-xs text-[color:var(--color-muted)]">{key.hint}</p>
                  <p className="text-sm text-[color:var(--color-muted)]">
                    {t('apiKeys.created', { date: formatNumericInstant(key.createdAt) })}
                    {key.lastUsedAt
                      ? t('apiKeys.lastUsed', { date: formatNumericInstant(key.lastUsedAt) })
                      : t('apiKeys.neverUsed')}
                    {key.expiresAt ? t('apiKeys.expires', { date: formatNumericInstant(key.expiresAt) }) : null}
                    {/* Which branch the key works in. Only worth saying to a
                        company that has more than one — for everybody else
                        every key is the same key. */}
                    {locations.length > 1
                      ? t('apiKeys.atLocation', {
                          name: locationNameById.get(key.locationId) ?? t('apiKeys.otherLocation'),
                        })
                      : null}
                  </p>
                  <p className="mt-0.5 font-mono text-xs break-words text-[color:var(--color-muted)]">
                    {key.scopes.join(' ')}
                  </p>
                </div>
                {status === 'active' ? (
                  <Button
                    variant="danger"
                    busy={revoke.isPending}
                    onClick={() => {
                      if (window.confirm(t('apiKeys.confirmRevoke', { name: key.name }))) revoke.mutate(key.id);
                    }}
                  >
                    {t('apiKeys.revoke')}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : keys.data ? (
        <p className="mb-4 text-[color:var(--color-muted)]">{t('apiKeys.emptyMessage')}</p>
      ) : null}

      {planGated ? null : (
        <>
          <div className="flex flex-wrap items-start gap-2">
            <div className="min-w-60 flex-1">
              <TextInput
                label={t('apiKeys.nameField')}
                hint={t('apiKeys.nameHint')}
                maxLength={API_KEY_NAME_MAX}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="min-w-52">
              <Select
                label={t('apiKeys.levelField')}
                hint={t('apiKeys.levelHint')}
                value={preset}
                options={[
                  { value: 'read', label: t('apiKeys.preset.read') },
                  { value: 'read_write', label: t('apiKeys.preset.read_write') },
                  { value: 'custom', label: t('apiKeys.preset.custom') },
                ]}
                onChange={(event) => setPreset(event.target.value as 'read' | 'read_write' | 'custom')}
              />
            </div>
            <div className="min-w-44">
              <Select
                label={t('apiKeys.expiryField')}
                hint={t('apiKeys.expiryHint')}
                value={expiresInDays}
                options={[
                  { value: '', label: t('apiKeys.expiryNever') },
                  ...API_KEY_EXPIRY_DAY_CHOICES.map((days) => ({
                    value: String(days),
                    label: t('apiKeys.expiryDays', { count: days }),
                  })),
                ]}
                onChange={(event) => setExpiresInDays(event.target.value)}
              />
            </div>
            <div className="mb-4">
              {/* Invisible label lines the button up with the inputs rather than their hints. */}
              <span className="field-label invisible" aria-hidden="true">
                {t('apiKeys.createKey')}
              </span>
              <Button
                variant="primary"
                busy={create.isPending}
                disabled={name.trim() === '' || chosenScopes.length === 0}
                onClick={() => create.mutate()}
              >
                {t('apiKeys.createKey')}
              </Button>
            </div>
          </div>

          {preset === 'custom' ? (
            <fieldset className="mb-4 rounded-md border border-[color:var(--color-line)] p-3">
              <legend className="px-1 text-sm font-medium">{t('apiKeys.scopesLegend')}</legend>
              <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('apiKeys.scopesHint')}</p>
              <div className="grid gap-x-4 sm:grid-cols-2">
                {scopeCatalog.map((descriptor) => (
                  <Checkbox
                    key={descriptor.scope}
                    label={descriptor.sensitive ? `${descriptor.scope} · ${t('apiKeys.sensitive')}` : descriptor.scope}
                    checked={custom.includes(descriptor.scope)}
                    onChange={(event) =>
                      setCustom((current) =>
                        event.target.checked
                          ? [...current, descriptor.scope]
                          : current.filter((scope) => scope !== descriptor.scope),
                      )
                    }
                  />
                ))}
              </div>
            </fieldset>
          ) : null}
        </>
      )}

      {/* ---- Connected apps ------------------------------------------------
          A different kind of credential on the same screen: an app the company
          authorized through "Sign in with", rather than a key somebody pasted.
          Listed separately because the thing to revoke is the connection, and
          because its access tokens last an hour and are nobody's business. */}
      {apps.data && apps.data.apps.length > 0 ? (
        <div className="mt-6 border-t border-[color:var(--color-line)] pt-4">
          <h3 className="mb-2 font-semibold">{t('apiKeys.connectedHeading')}</h3>
          <ul className="divide-y divide-[color:var(--color-line)]">
            {apps.data.apps.map((app) => {
              const status = statusOf(app);
              return (
                <li key={app.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                  <div>
                    <p className="font-medium">
                      {app.clientName}
                      {/* The list keeps ended connections — that history is the
                        point — so each row has to say which it is, or a
                        disconnect refetches into what looks like the same live
                        row and reconnecting stacks up rows nobody can tell
                        apart. */}
                      {status !== 'active' ? (
                        <span className="ml-2 text-sm font-normal text-[color:var(--color-danger)]">
                          {t(`apiKeys.status.${status}`)}
                        </span>
                      ) : null}
                    </p>
                    <p className="text-sm text-[color:var(--color-muted)]">
                      {t('apiKeys.connected', { date: formatNumericInstant(app.createdAt) })}
                      {app.lastUsedAt
                        ? t('apiKeys.lastUsed', { date: formatNumericInstant(app.lastUsedAt) })
                        : t('apiKeys.neverUsed')}
                    </p>
                    <p className="mt-0.5 font-mono text-xs break-words text-[color:var(--color-muted)]">
                      {app.scopes.join(' ')}
                    </p>
                  </div>
                  {status === 'active' ? (
                    <Button
                      variant="danger"
                      busy={disconnect.isPending}
                      onClick={() => {
                        if (window.confirm(t('apiKeys.confirmDisconnect', { name: app.clientName }))) {
                          disconnect.mutate(app.id);
                        }
                      }}
                    >
                      {t('apiKeys.disconnect')}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {surface.length > 0 ? (
        <div className="mt-4 border-t border-[color:var(--color-line)] pt-3">
          <button
            type="button"
            className="text-sm font-medium text-[color:var(--color-brand-strong)]"
            aria-expanded={showSurface}
            onClick={() => setShowSurface((open) => !open)}
          >
            {showSurface ? t('apiKeys.hideSurface') : t('apiKeys.showSurface', { count: surface.length })}
          </button>
          {showSurface ? (
            <>
              <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('apiKeys.surfaceDescription')}</p>
              <ul className="mt-2 space-y-1">
                {surface.map((entry) => (
                  <li key={`${entry.method} ${entry.path}`} className="font-mono text-xs">
                    {entry.method} {entry.path}
                    <span className="ml-2 text-[color:var(--color-muted)]">{entry.scope}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('apiKeys.surfaceDocsHint')}</p>
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
